<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { format } from 'svelte-i18n';
	import { real_id } from 'src/helpers/interface_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import {
		getSensorComponentKey,
		readSensorComponentName,
		SENSOR_SET_CONFIG_METHOD,
		type VentoraSensorKind,
	} from 'src/helpers/sensor_config_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { getDeviceSettings, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { Button, Input, Text } from '@Shelly/shelly-design-system';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Device } from 'src/types/Device';

	export let device: Device;
	export let sensorKind: VentoraSensorKind;

	const deviceId = real_id(device.id);
	const componentKey = getSensorComponentKey(sensorKind);
	const setConfigMethod = SENSOR_SET_CONFIG_METHOD[sensorKind];

	const nameLabelByKind: Record<VentoraSensorKind, { key: string; default: string }> = {
		temperature: {
			key: 'ventora_sensor_name_temperature',
			default: 'Temperature sensor name',
		},
		humidity: {
			key: 'ventora_sensor_name_humidity',
			default: 'Humidity sensor name',
		},
		illuminance: {
			key: 'ventora_sensor_name_illuminance',
			default: 'Illuminance sensor name',
		},
	};

	const descriptionByKind: Record<VentoraSensorKind, { key: string; default: string }> = {
		temperature: {
			key: 'ventora_sensor_name_temperature_description',
			default: 'Display name for the built-in temperature sensor.',
		},
		humidity: {
			key: 'ventora_sensor_name_humidity_description',
			default: 'Display name for the built-in humidity sensor.',
		},
		illuminance: {
			key: 'ventora_sensor_name_illuminance_description',
			default: 'Display name for the built-in illuminance sensor.',
		},
	};

	let value = '';
	let cachedName = '';
	let loading = false;
	let isDirty = false;

	$: settingsStore = getDeviceSettings(deviceId);
	$: deviceSettings = $settingsStore;
	$: storeName = readSensorComponentName(deviceSettings, sensorKind);
	$: nameLabel = nameLabelByKind[sensorKind];
	$: description = descriptionByKind[sensorKind];

	$: if (!loading && !isDirty && deviceSettings?.[componentKey] !== undefined) {
		value = storeName;
		cachedName = storeName;
	}

	const handleInput = () => {
		isDirty = true;
	};

	const saveName = async () => {
		const trimmedName = value.trim();
		if (trimmedName === '' || trimmedName === cachedName) {
			return;
		}

		loading = true;
		const params = {
			id: device.channel,
			config: { name: trimmedName },
		};
		const res: SettingRequest = await gen2Request(device, setConfigMethod, params);
		const response = onSettingsResponse(res, device);

		if (response) {
			const currentSettings = readDeviceSettings(deviceId);
			devices_settings.update_devices_settings(deviceId, {
				...currentSettings,
				[componentKey]: {
					...(currentSettings?.[componentKey] ?? {}),
					name: trimmedName,
				},
			});
			value = trimmedName;
			cachedName = trimmedName;
			isDirty = false;
		} else {
			value = cachedName;
			isDirty = false;
		}
		loading = false;
	};
</script>

<Text styleType="body-small" class="mb-s">
	{$format(description.key, { default: description.default })}
</Text>

<Input
	size="m"
	class="mb-s"
	label={$format(nameLabel.key, { default: nameLabel.default })}
	bind:value
	on:input={handleInput}
	on:enter={saveName}
	id="input-sensor-name-{sensorKind}"
	aria-label={$format(nameLabel.key, { default: nameLabel.default })}
/>

<Button class="mt-s" {loading} size="m" width="full" on:click={saveName}>
	{$format('button_save', { default: 'Save' })}
</Button>
